// Story 11.2 — Transformación pura `FlowDef → { nodes, edges }`. Sin
// dependencias de Reactflow para que los tests corran sin instalar.

export type DiagramNodeKind =
  | 'send-message'
  | 'wait'
  | 'condition'
  | 'db-action'
  | 'ts-action'
  | 'component'
  | 'goto-ghost';

export interface DiagramNode {
  id: string;
  kind: DiagramNodeKind;
  label: string;
  /** Texto secundario, ej. el body resumido del mensaje. */
  detail?: string;
  /** True para `goto-ghost` que apunta a otro flow. */
  external?: boolean;
}

export type EdgeKind = 'sequence' | 'branch' | 'goto' | 'else';

export interface DiagramEdge {
  id: string;
  source: string;
  target: string;
  kind: EdgeKind;
  label?: string;
  /** True para edges puenteadas (visual: dashes). */
  dashed?: boolean;
}

export interface DiagramGraph {
  nodes: DiagramNode[];
  edges: DiagramEdge[];
}

/** Shape mínima de un Flow esperado por el transformer. Espejo de
 *  `FlowDef` de `@revelation-bots/core` pero sin importar el package — así
 *  el frontend puede aceptar cualquier objeto post-parser. */
export interface FlowShape {
  steps: StepShape[];
}

export type StepShape = { id: string } & Partial<{
  sendMessage: SendMessageShape;
  waitFor: string;
  conditions: ConditionBranchShape[];
  action: ActionShape;
  goto: string;
  useComponent: string;
}>;

export interface SendMessageShape {
  type?: string;
  body?: string;
  text?: string;
  caption?: string;
}

export interface ActionShape {
  type?: string;
  target?: string;
  table?: string;
  /** ts_action solo: nombre del archivo en `ext/`. */
  file?: string;
}

export interface ConditionBranchShape {
  if?: string;
  else?: boolean;
  goto?: string;
  sendMessage?: SendMessageShape;
  action?: ActionShape;
}

const TRUNCATE_LEN = 40;

/** Trunca y normaliza para mostrarlo como detail/label. */
function truncate(input: string | undefined, len = TRUNCATE_LEN): string | undefined {
  if (input == null) return undefined;
  const flat = input.replace(/\s+/g, ' ').trim();
  if (flat.length <= len) return flat;
  return `${flat.slice(0, len - 1)}…`;
}

/** Etiqueta legible de una rama de condición. */
export function serializeCondition(branch: ConditionBranchShape): string {
  if (branch.else) return 'else';
  if (branch.if?.trim()) return truncate(branch.if, 50) ?? branch.if;
  return 'rama';
}

function detectKind(step: StepShape): DiagramNodeKind {
  // Story 11.2 AC #3: si el step tiene `conditions`, gana visualmente
  // sobre `waitFor` (en el DSL real `wait_for + conditions` es el patrón
  // canónico). El branching es el interés visual.
  if (step.conditions !== undefined && step.conditions.length > 0) return 'condition';
  if (step.sendMessage !== undefined) return 'send-message';
  if (step.waitFor !== undefined) return 'wait';
  if (step.useComponent !== undefined) return 'component';
  if (step.action !== undefined) {
    const t = step.action.type ?? '';
    if (t.startsWith('db_') || t === 'db') return 'db-action';
    if (t === 'ts_action' || t === 'ts') return 'ts-action';
    return 'db-action'; // fallback
  }
  // goto sin acción adicional es solo un edge — el caller lo skipea.
  return 'send-message';
}

function buildLabel(step: StepShape, kind: DiagramNodeKind): string {
  return step.id || `(${kind})`;
}

function buildDetail(step: StepShape, kind: DiagramNodeKind): string | undefined {
  if (kind === 'send-message') {
    const m = step.sendMessage;
    if (!m) return undefined;
    return truncate(m.body ?? m.text ?? m.caption);
  }
  if (kind === 'wait') {
    const w = step.waitFor ?? '';
    return truncate(w);
  }
  if (kind === 'db-action' || kind === 'ts-action') {
    const a = step.action;
    if (!a) return undefined;
    const head = a.type ?? '';
    const body = a.table ?? a.file ?? a.target ?? '';
    const text = body ? `${head}: ${body}` : head;
    // Story 15-6 — el server no ejecuta `ts_action` (se rechaza al guardar).
    return truncate(kind === 'ts-action' ? `⚠ solo local · ${text}` : text);
  }
  if (kind === 'component') {
    return truncate(`use_component: ${step.useComponent ?? ''}`);
  }
  if (kind === 'condition') {
    const n = step.conditions?.length ?? 0;
    return `${n} ramas`;
  }
  return undefined;
}

/**
 * Genera el grafo del flow. Edges entre steps consecutivos son `sequence`.
 * `condition.branches` emite N edges con label de la condición; las que
 * tengan `goto` apuntan al ghost target. `step.goto` (sin sendMessage) NO
 * crea nodo: la edge sale del step previo al ghost del flow target.
 */
export function flowToGraph(flow: FlowShape): DiagramGraph {
  const nodes: DiagramNode[] = [];
  const edges: DiagramEdge[] = [];
  const ghostNodes = new Map<string, string>(); // flowId → ghostNodeId
  const stepIds: string[] = []; // ids de los nodos visibles, en orden

  function ensureGhost(flowId: string): string {
    const existing = ghostNodes.get(flowId);
    if (existing) return existing;
    const id = `ghost::${flowId}`;
    ghostNodes.set(flowId, id);
    nodes.push({
      id,
      kind: 'goto-ghost',
      label: `→ ${flowId}`,
      detail: 'goto',
      external: true,
    });
    return id;
  }

  for (let i = 0; i < flow.steps.length; i++) {
    const step = flow.steps[i];
    const stepNodeId = step.id || `step-${i}`;

    // Si el step es un goto puro (sin sendMessage/action/conditions/wait),
    // NO emitimos nodo; agregamos una edge desde el step anterior al ghost.
    const isPureGoto =
      step.goto !== undefined &&
      step.sendMessage === undefined &&
      step.action === undefined &&
      step.conditions === undefined &&
      step.waitFor === undefined &&
      step.useComponent === undefined;

    if (isPureGoto) {
      const targetGhost = ensureGhost(step.goto as string);
      const prev = stepIds[stepIds.length - 1];
      if (prev) {
        edges.push({
          id: `e:${prev}->${targetGhost}`,
          source: prev,
          target: targetGhost,
          kind: 'goto',
          dashed: true,
          label: `goto ${step.goto}`,
        });
      }
      // No registramos stepIds (el goto no tiene continuación lineal).
      continue;
    }

    const kind = detectKind(step);
    nodes.push({
      id: stepNodeId,
      kind,
      label: buildLabel(step, kind),
      detail: buildDetail(step, kind),
    });

    // Edge implícita desde el step previo (en orden), pero NO desde nodos
    // condition (esos manejan su propia salida via branches).
    const prev = stepIds[stepIds.length - 1];
    if (prev) {
      const prevNode = nodes.find((n) => n.id === prev);
      if (prevNode && prevNode.kind !== 'condition') {
        edges.push({
          id: `e:${prev}->${stepNodeId}`,
          source: prev,
          target: stepNodeId,
          kind: 'sequence',
        });
      }
    }
    stepIds.push(stepNodeId);

    // Si el step tiene `conditions`, emitimos edges desde este nodo a sus
    // branches. Cada branch con `goto` apunta al ghost; sino, apunta al
    // siguiente step "after" (resolución diferida — usamos un placeholder).
    if (step.conditions && step.conditions.length > 0) {
      const next = flow.steps[i + 1];
      const fallthroughId = next?.id || (next ? `step-${i + 1}` : null);
      for (let b = 0; b < step.conditions.length; b++) {
        const branch = step.conditions[b];
        const label = serializeCondition(branch);
        const isElse = branch.else === true;
        const target =
          branch.goto !== undefined ? ensureGhost(branch.goto) : (fallthroughId ?? stepNodeId);
        edges.push({
          id: `e:${stepNodeId}-branch-${b}->${target}`,
          source: stepNodeId,
          target,
          kind: isElse ? 'else' : 'branch',
          label,
        });
      }
    } else if (step.goto !== undefined) {
      // step con goto + otro action — emite edge punteada extra al ghost.
      const targetGhost = ensureGhost(step.goto);
      edges.push({
        id: `e:${stepNodeId}->${targetGhost}`,
        source: stepNodeId,
        target: targetGhost,
        kind: 'goto',
        dashed: true,
        label: `goto ${step.goto}`,
      });
    }
  }

  return { nodes, edges };
}
